import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10Co, CustomButton10Ca, CustomButton10 } from '../../Templates';
import Spinner from '../../common/Spinner';
import { 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaTruck, 
  FaMapMarkerAlt,
  FaBox,
  FaCalendarAlt,
  FaTimesCircle,
  FaLocationArrow
} from 'react-icons/fa';

const DeliveryCountdownStatus = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showNotReceivedDialog, setShowNotReceivedDialog] = useState(false);
  const [showConfirmDeliveryDialog, setShowConfirmDeliveryDialog] = useState(false);
  const [requestedExtensionMinutes, setRequestedExtensionMinutes] = useState(60);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Determine user roles for delivery
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Calculate remaining time until delivery
  useEffect(() => {
    if (!delivery?.delivery_scheduled_time) return;

    const updateTimer = () => {
      const scheduledTime = new Date(delivery.delivery_scheduled_time);
      const now = new Date();
      const remaining = Math.max(0, scheduledTime.getTime() - now.getTime());
      
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [delivery?.delivery_scheduled_time]);

  // Format time remaining
  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return t('package_actions.delivery_time_reached');
    
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Get current location for location proof
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('package_actions.geolocation_not_supported')));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          if (accuracy > 50) {
            reject(new Error(t('package_actions.location_accuracy_low')));
            return;
          }

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: data.display_name || t('package_actions.address_not_available')
            });
          } catch (error) {
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: t('package_actions.address_lookup_failed')
            });
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle confirm delivery (carrier only)
  const handleConfirmDelivery = async (withLocation = false) => {
    setLoading(true);
    
    try {
      let locationProof = null;
      
      // Get location if requested
      if (withLocation) {
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('package_actions.location_not_available'),
            description: locError.message || t('package_actions.location_not_available_desc')
          });
        }
      }

      const updateData = {
        status: 'package_delivered',
        delivery_confirmed_at: new Date().toISOString(),
        delivery_confirmed_by: currentUser.id,
        updated_at: new Date().toISOString()
      };

      // Add location proof if available
      if (locationProof) {
        updateData.delivery_completion_location_proof = locationProof;
      }

      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Create notification for sender
      await createNotification(
        delivery.sender_id,
        'package_delivered',
        t('package_actions.package_delivered_notification'),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.package_delivered_system') + 
        (locationProof ? ' ' + t('package_actions.location_shared') : '')
      );

      addToast({
        type: 'success',
        title: t('package_actions.delivery_confirmed'),
        description: t('package_actions.package_delivered_successfully')
      });

      setShowConfirmDeliveryDialog(false);

    } catch (error) {
      console.error('Error confirming delivery:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_confirming_delivery')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle request more time (carrier only)
  const handleRequestMoreTime = async () => {
    setLoading(true);
    
    try {
      const extensionData = {
        requested_by: currentUser.id,
        requested_at: new Date().toISOString(),
        additional_minutes: requestedExtensionMinutes,
        status: 'pending'
      };

      const currentExtensions = delivery.delivery_extension_requests || [];
      const updatedExtensions = [...currentExtensions, extensionData];

      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          delivery_extension_requests: updatedExtensions,
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Create notification for sender
      await createNotification(
        delivery.sender_id,
        'delivery_extension_request',
        t('package_actions.delivery_extension_request_notification', { 
          minutes: requestedExtensionMinutes 
        }),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.delivery_extension_request_system', { 
          minutes: requestedExtensionMinutes 
        })
      );

      addToast({
        type: 'info',
        title: t('package_actions.extension_requested'),
        description: t('package_actions.waiting_for_approval')
      });

      setShowExtensionDialog(false);

    } catch (error) {
      console.error('Error requesting delivery extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_requesting_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle extension response (sender only)
  const handleExtensionResponse = async (approve, extensionRequest) => {
    setLoading(true);
    
    try {
      const updatedExtensions = (delivery.delivery_extension_requests || []).map(req => 
        req.requested_at === extensionRequest.requested_at 
          ? { ...req, status: approve ? 'approved' : 'denied', responded_at: new Date().toISOString() }
          : req
      );

      const updateData = {
        delivery_extension_requests: updatedExtensions,
        updated_at: new Date().toISOString()
      };

      if (approve) {
        // Extend the delivery time
        const currentTime = new Date(delivery.delivery_scheduled_time);
        const extendedTime = new Date(currentTime.getTime() + (extensionRequest.additional_minutes * 60 * 1000));
        
        updateData.delivery_scheduled_time = extendedTime.toISOString();
        updateData.delivery_extension_minutes = (delivery.delivery_extension_minutes || 0) + extensionRequest.additional_minutes;
      }

      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Create notification for carrier
      await createNotification(
        extensionRequest.requested_by,
        approve ? 'delivery_extension_approved' : 'delivery_extension_denied',
        approve ? 
          t('package_actions.delivery_extension_approved_notification', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.delivery_extension_denied_notification'),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        approve ? 
          t('package_actions.delivery_extension_approved_system', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.delivery_extension_denied_system')
      );

      addToast({
        type: approve ? 'success' : 'info',
        title: approve ? t('package_actions.extension_approved') : t('package_actions.extension_denied'),
        description: approve ? 
          t('package_actions.delivery_time_extended') : 
          t('package_actions.extension_was_denied')
      });

    } catch (error) {
      console.error('Error responding to extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_responding_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle package not received (sender only)
  const handlePackageNotReceived = async () => {
    setLoading(true);
    
    try {
      const updateData = {
        status: 'delivery_disputed',
        delivery_disputed: true,
        delivery_dispute_details: {
          disputed_by: currentUser.id,
          disputed_at: new Date().toISOString(),
          reason: 'package_not_received',
          message: t('package_actions.package_not_received_dispute')
        },
        updated_at: new Date().toISOString()
      };

      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Create notification for carrier
      await createNotification(
        delivery.carrier_id,
        'delivery_disputed',
        t('package_actions.delivery_disputed_notification'),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.delivery_disputed_system')
      );

      addToast({
        type: 'warning',
        title: t('package_actions.delivery_disputed'),
        description: t('package_actions.dispute_submitted')
      });

      setShowNotReceivedDialog(false);

    } catch (error) {
      console.error('Error reporting package not received:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_reporting_issue')
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if time has expired
  const isTimeExpired = timeRemaining <= 0;

  // Get pending extension request
  const pendingExtension = (delivery.delivery_extension_requests || [])
    .find(req => req.status === 'pending');

  // Render countdown timer
  const renderCountdownTimer = () => {
    return (
      <div className="PAC-countdown-section">
        <div className="PAC-countdown-title">
          <FaClock size={12} />
          {isTimeExpired ? t('package_actions.delivery_time_reached') : t('package_actions.delivery_countdown')}
        </div>
        <div className={`PAC-delivery-countdown ${isTimeExpired ? 'expired' : ''}`} 
             style={{ color: isTimeExpired ? '#10B981' : 'var(--accent-color2)' }}>
          {formatTimeRemaining(timeRemaining)}
        </div>
      </div>
    );
  };

  // Render delivery details
  const renderDeliveryDetails = () => {
    if (!delivery?.delivery_scheduled_time) return null;

    return (
      <div className="PAC-location-section">
        <div className="PAC-location-title">
          <FaCalendarAlt size={12} />
          {t('package_actions.delivery_details')}
        </div>
        <div className="PAC-location-address">
          <strong>{t('package_actions.scheduled_time')}:</strong> {new Date(delivery.delivery_scheduled_time).toLocaleString()}
        </div>
        <div className="PAC-location-address">
          <strong>{t('package_actions.time_format')}:</strong> {delivery.delivery_scheduled_days}d {delivery.delivery_scheduled_hours}h {t('package_actions.from_pickup')}
        </div>
        {delivery.delivery_extension_minutes > 0 && (
          <div className="PAC-location-address" style={{ color: 'var(--accent-color2)' }}>
            <strong>{t('package_actions.extended_by')}:</strong> {delivery.delivery_extension_minutes}m
          </div>
        )}
      </div>
    );
  };

  // Render extension request handling (for sender)
  const renderExtensionRequest = () => {
    if (!pendingExtension || pendingExtension.requested_by === currentUser?.id) return null;

    return (
      <div className="PAC-extension-section">
        <div className="PAC-extension-title">
          {t('package_actions.extension_request')}
        </div>
        <div className="PAC-extension-message">
          {t('package_actions.carrier_requested_extension', { 
            minutes: pendingExtension.additional_minutes 
          })}
        </div>
        <div className="PAC-action-buttons-v2">
          <CustomButton10Co
            onClick={() => handleExtensionResponse(true, pendingExtension)}
            disabled={loading}
          >
            {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.approve')}
          </CustomButton10Co>
          <CustomButton10Ca
            onClick={() => handleExtensionResponse(false, pendingExtension)}
            disabled={loading}
          >
            {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.deny')}
          </CustomButton10Ca>
        </div>
      </div>
    );
  };

  return (
    <div className="PAC-meeting-section">
      <div className="PAC-meeting-title">
        <FaTruck size={14} />
        {t('package_actions.delivery_in_progress')}
      </div>
      
      {renderDeliveryDetails()}
      {renderCountdownTimer()}
      {renderExtensionRequest()}
      
      {/* Sender view */}
      {isPackageSender && !pendingExtension && (
        <div className="PAC-action-buttons-v2">
          {isTimeExpired && (
            <CustomButton10Ca
              onClick={() => setShowNotReceivedDialog(true)}
              label={t('package_actions.package_not_received')}
              disabled={loading}
            >
              <FaTimesCircle />
            </CustomButton10Ca>
          )}
          {!isTimeExpired && (
            <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--secondary-color)' }}>
              {t('package_actions.waiting_for_delivery')}
            </div>
          )}
        </div>
      )}
      
      {/* Carrier view */}
      {isCarrier && !pendingExtension && (
        <div className="PAC-action-buttons-v2">
          <CustomButton10Co
            onClick={() => setShowConfirmDeliveryDialog(true)}
            label={t('package_actions.confirm_delivery')}
            disabled={loading}
          >
            <FaBox />
          </CustomButton10Co>
          
          <CustomButton10
            onClick={() => setShowExtensionDialog(true)}
            label={t('package_actions.request_more_time')}
            disabled={loading}
          >
            <FaClock />
          </CustomButton10>
        </div>
      )}
      
      {/* Show pending extension status */}
      {pendingExtension && pendingExtension.requested_by === currentUser?.id && (
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--accent-color2)', marginTop: '8px' }}>
          {t('package_actions.extension_pending_approval')}
        </div>
      )}
      
      {/* Confirm Delivery Dialog */}
      {showConfirmDeliveryDialog && (
        <ConfirmDeliveryDialog
          onCancel={() => setShowConfirmDeliveryDialog(false)}
          onConfirm={handleConfirmDelivery}
          isConfirming={loading}
          packageData={packageData}
        />
      )}
      
      {/* Extension Request Dialog */}
      {showExtensionDialog && (
        <ExtensionRequestDialog
          onCancel={() => setShowExtensionDialog(false)}
          onConfirm={handleRequestMoreTime}
          isRequesting={loading}
          requestedMinutes={requestedExtensionMinutes}
          setRequestedMinutes={setRequestedExtensionMinutes}
        />
      )}
      
      {/* Package Not Received Dialog */}
      {showNotReceivedDialog && (
        <PackageNotReceivedDialog
          onCancel={() => setShowNotReceivedDialog(false)}
          onConfirm={handlePackageNotReceived}
          isReporting={loading}
        />
      )}
    </div>
  );
};

// Confirm Delivery Dialog Component
const ConfirmDeliveryDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  packageData
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  const handleConfirm = async () => {
    if (shareLocation) {
      setGettingLocation(true);
    }
    
    try {
      await onConfirm(shareLocation);
    } finally {
      setGettingLocation(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="PAC-modal-overlay-v2" onClick={(e) => {
      if (e.target === e.currentTarget && !isConfirming) {
        onCancel();
      }
    }}>
      <div className="PAC-modal2-v2">
        <div className="PAC-dialog-content">
          <h4>{t('package_actions.confirm_delivery')}</h4>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <FaBox size={32} style={{ color: 'var(--accent-color)' }} />
          </div>
          <p>{t('package_actions.confirm_delivery_message')}</p>
          
          <div className="PAC-location-section">
            <div className="PAC-location-title">
              <FaMapMarkerAlt size={12} />
              {t('package_actions.delivery_location')}
            </div>
            <div className="PAC-location-address">
              {packageData ? `${packageData.address_to_street}, ${packageData.address_to_city}` : t('package_actions.delivery_address')}
            </div>
          </div>
          
          <div className="PAC-location-option">
            <label className="PAC-checkbox-label">
              <input
                type="checkbox"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
                disabled={isConfirming}
              />
              <span>{t('package_actions.share_location_proof')}</span>
            </label>
            <p className="PAC-location-help">
              {t('package_actions.optional_delivery_proof')}
            </p>
          </div>
          
          <div className="PAC-dialog-actions">
            <CustomButton10Ca
              onClick={onCancel}
              label={t('buttons.cancel')}
              disabled={isConfirming}
            />
            <CustomButton10Co
              onClick={handleConfirm}
              disabled={isConfirming || gettingLocation}
            >
              {isConfirming || gettingLocation ? (
                <Spinner size="small" showText={false} />
              ) : (
                t('package_actions.confirm_delivered')
              )}
            </CustomButton10Co>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Extension Request Dialog Component
const ExtensionRequestDialog = ({
  onCancel,
  onConfirm,
  isRequesting,
  requestedMinutes,
  setRequestedMinutes
}) => {
  const { t } = useTranslation('common');

  return ReactDOM.createPortal(
    <div className="PAC-modal-overlay-v2" onClick={(e) => {
      if (e.target === e.currentTarget && !isRequesting) {
        onCancel();
      }
    }}>
      <div className="PAC-modal2-v2">
        <div className="PAC-dialog-content">
          <h4>{t('package_actions.request_more_time')}</h4>
          <p>{t('package_actions.extension_request_message')}</p>
          
          <div className="PAC-extension-options">
            <label>{t('package_actions.additional_time')}:</label>
            <select
              value={requestedMinutes}
              onChange={(e) => setRequestedMinutes(parseInt(e.target.value))}
              disabled={isRequesting}
            >
              <option value={30}>30 {t('package_actions.minutes')}</option>
              <option value={60}>1 {t('package_actions.hour')}</option>
              <option value={120}>2 {t('package_actions.hours')}</option>
              <option value={180}>3 {t('package_actions.hours')}</option>
              <option value={360}>6 {t('package_actions.hours')}</option>
              <option value={720}>12 {t('package_actions.hours')}</option>
              <option value={1440}>1 {t('package_actions.day')}</option>
            </select>
          </div>
          
          <div className="PAC-dialog-actions">
            <CustomButton10Ca
              onClick={onCancel}
              label={t('buttons.cancel')}
              disabled={isRequesting}
            />
            <CustomButton10Co
              onClick={onConfirm}
              disabled={isRequesting}
            >
              {isRequesting ? (
                <Spinner size="small" showText={false} />
              ) : (
                t('package_actions.request_extension')
              )}
            </CustomButton10Co>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Package Not Received Dialog Component
const PackageNotReceivedDialog = ({
  onCancel,
  onConfirm,
  isReporting
}) => {
  const { t } = useTranslation('common');

  return ReactDOM.createPortal(
    <div className="PAC-modal-overlay-v2" onClick={(e) => {
      if (e.target === e.currentTarget && !isReporting) {
        onCancel();
      }
    }}>
      <div className="PAC-modal2-v2">
        <div className="PAC-dialog-content">
          <h4>{t('package_actions.package_not_received')}</h4>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <FaExclamationTriangle size={32} style={{ color: '#F59E0B' }} />
          </div>
          <p>{t('package_actions.package_not_received_message')}</p>
          <div className="PAC-warning-text">
            {t('package_actions.this_will_dispute_delivery')}
          </div>
          <div className="PAC-dialog-actions">
            <CustomButton10Ca
              onClick={onCancel}
              label={t('buttons.cancel')}
              disabled={isReporting}
            />
            <CustomButton10Co
              onClick={onConfirm}
              disabled={isReporting}
            >
              {isReporting ? (
                <Spinner size="small" showText={false} />
              ) : (
                t('package_actions.report_not_received')
              )}
            </CustomButton10Co>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeliveryCountdownStatus;
